import { LitElement, css, html } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';

@customElement('lit-animated-snow2')
export class LitAnimatedSnow2 extends LitElement {
    @property({ type: String }) type: 'snow' | 'sakura' | 'spring' = 'spring';
    @property({ type: Number }) snowRatio = 0.1;

    @query('canvas') private canvas!: HTMLCanvasElement;
    private ctx!: CanvasRenderingContext2D | null;
    private animationFrameId = 0;

    // Cache dla jednego, idealnego płatka sakury
    private sakuraCache: HTMLCanvasElement | null = null;

    private flakes: Array<{
        kind: 'snow' | 'sakura';
        x: number;
        y: number;
        z: number; // z = głębia (0-1)
        r: number;
        s: number;
        rot: number;
        rotSpeed: number;
        drift: number;
    }> = [];

    private lastWidth = 0;
    private lastHeight = 0;

    static styles = css`
        :host {
            position: absolute;
            inset: 0;
            pointer-events: none;
            display: block;
            z-index: 1;
        }
        canvas {
            width: 100%;
            height: 100%;
            display: block;
        }
    `;

    render() {
        return html`<canvas></canvas>`;
    }

    firstUpdated() {
        this.ctx = this.canvas.getContext('2d', { alpha: true });
        this.createSakuraCache(); // Renderujemy 🌸 tylko raz do pamięci
        this.resizeCanvas();
        this.createFlakes();
        this.loop();
        window.addEventListener('resize', this.resizeCanvas);
    }

    // Tworzy statyczny obraz 🌸, co jest 10x szybsze niż rysowanie tekstu co klatkę
    private createSakuraCache() {
        const offCanvas = document.createElement('canvas');
        offCanvas.width = 64;
        offCanvas.height = 64;
        const oCtx = offCanvas.getContext('2d')!;
        oCtx.font = '48px serif';
        oCtx.textAlign = 'center';
        oCtx.textBaseline = 'middle';
        oCtx.fillText('🌸', 32, 32);
        this.sakuraCache = offCanvas;
    }

    private resizeCanvas = () => {
        const rect = this.getBoundingClientRect();
        this.lastWidth = rect.width;
        this.lastHeight = rect.height;
        const dpr = window.devicePixelRatio || 1;
        this.canvas.width = this.lastWidth * dpr;
        this.canvas.height = this.lastHeight * dpr;
        this.ctx?.scale(dpr, dpr);
    };

    private createFlakes() {
        const W = this.lastWidth;
        const H = this.lastHeight;
        if (!W || !H) return;

        const area = W * H;
        const count = Math.max(40, Math.round(area / 2800));

        this.flakes = Array.from({ length: count }, () => {
            const isSnow = this.type === 'snow' || (this.type === 'spring' && Math.random() < this.snowRatio);
            const z = Math.random(); // 0 = tło, 1 = pierwszy plan

            return {
                kind: isSnow ? 'snow' : 'sakura',
                x: Math.random() * W,
                y: Math.random() * H,
                z: z,
                // Skalowanie 3D: r i s zależą od głębi Z
                r: isSnow ? 1 + z * 1.5 : 4 + z * 5,
                s: isSnow ? 0.4 + z * 1.0 : 0.2 + z * 0.5,
                rot: Math.random() * Math.PI * 2,
                rotSpeed: isSnow ? 0 : (Math.random() - 0.5) * 0.05,
                drift: (Math.random() - 0.5) * 0.3,
            };
        });
    }

    private draw() {
        if (!this.ctx || !this.sakuraCache) return;
        const { ctx, lastWidth: W, lastHeight: H } = this;
        ctx.clearRect(0, 0, W, H);

        for (const f of this.flakes) {
            // Fizyka ruchu z efektem paralaksy
            f.y += f.s;
            f.x += Math.sin(f.y * 0.01 + f.z) * 0.4 + f.drift;

            if (f.kind === 'snow') {
                ctx.globalAlpha = 0.3 + f.z * 0.6; // Głębia przez przezroczystość
                ctx.beginPath();
                ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2);
                ctx.fillStyle = '#FFF';
                ctx.fill();
            } else {
                const flip = Math.abs(Math.cos(f.rot));
                const size = f.r * 2.5;

                ctx.save();
                ctx.translate(f.x, f.y);
                ctx.rotate(f.rot);
                ctx.scale(flip, 1); // Efekt obracającego się płatka 3D
                ctx.globalAlpha = 0.5 + f.z * 0.4;

                // Rysowanie z cache (drawImage jest ultra wydajne)
                ctx.drawImage(this.sakuraCache, -size / 2, -size / 2, size, size);
                ctx.restore();

                f.rot += f.rotSpeed;
            }

            // Zapętlenie płatka
            if (f.y > H + 15) {
                f.y = -15;
                f.x = Math.random() * W;
            }
            if (f.x > W + 15) f.x = -15;
            else if (f.x < -15) f.x = W + 15;
        }
        ctx.globalAlpha = 1.0;
    }

    private loop = () => {
        this.draw();
        this.animationFrameId = requestAnimationFrame(this.loop);
    };

    disconnectedCallback() {
        super.disconnectedCallback();
        cancelAnimationFrame(this.animationFrameId);
        window.removeEventListener('resize', this.resizeCanvas);
    }
}
